import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { HiOutlineSearch, HiArrowRight, HiCheck, HiOutlineHome } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { searchProducts, getProductsBySymptom, fetchBestsellers, fetchAllDiscounts } from '../../services/apiService';
import { getOptimizedImageUrl } from '../../utils/imageUtils';
import { motion, AnimatePresence } from 'framer-motion';
import { scrollToTop } from '../../utils/scrollUtils';
import PriceDisplay from '../common/PriceDisplay';
import { getPriceShape, formatCHF } from '../../utils/priceUtils';
import HeroShowcase from './HeroShowcase';
import HeroHelpStrip from './HeroHelpStrip';
import { RedSurfaceDecor } from './landingUi';
import { useCategoryBubbles } from '../../hooks/useCategoryBubbles';
import { goToCategory } from '../../utils/categoryUtils';
import { setClickSource } from '../../services/analytics/analyticsService';

// Constants
const TYPEWRITER_SPEED = 100;
const TYPEWRITER_DELETE_SPEED = 30;
const TYPEWRITER_PAUSE_TIME = 2000;
const SEARCH_DEBOUNCE_MS = 150;
const SEARCH_MIN_LENGTH = 2;
// How many bestsellers and offers the hero pulls. The desktop showcase loops
// each list in its own lane (see HeroShowcase); the mobile rail shows the first
// eight bestsellers. Teasers for the full sections further down the page.
const HERO_BESTSELLER_COUNT = 18;
const HERO_DEAL_COUNT = 16;

// Per-chip lean and vertical nudge for the desktop halo. Horizontal packing
// is left to flexbox — hand-picked left/right percentages could not account
// for how wide a translated category name renders, so chips overlapped.
const BUBBLE_JITTER = [
  { y: -6, rotate: -5 },
  { y: 4, rotate: 4 },
  { y: -9, rotate: -3 },
  { y: 5, rotate: 5 },
  { y: -4, rotate: -4 },
  { y: 8, rotate: 6 },
  { y: -6, rotate: -5 },
];

const HeroComponent = () => {
  const { t, language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [previewResults, setPreviewResults] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [didYouMean, setDidYouMean] = useState(null);
  const [matchedSymptom, setMatchedSymptom] = useState(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [showBubbles, setShowBubbles] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [announceSearchResults, setAnnounceSearchResults] = useState('');
  const [heroHeight, setHeroHeight] = useState('100dvh');
  const [bestsellers, setBestsellers] = useState([]);
  const [bestsellersLoading, setBestsellersLoading] = useState(true);
  const [deals, setDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(true);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const heroRef = useRef(null);
  const searchAbortControllerRef = useRef(null);
  const prefersReducedMotion = useRef(
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  // Desktop and mobile type different things. Mobile keeps the headline
  // typewriter ("Find your … Vitamins"); desktop types example searches
  // inside the search field itself, so its list is concrete queries.
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const TYPEWRITER_WORDS = useMemo(() => {
    const list = isDesktop ? t('hero.searchExamples') : t('hero.typewriterWords');
    return Array.isArray(list) && list.length ? list : [''];
  }, [t, language, isDesktop]); // eslint-disable-line react-hooks/exhaustive-deps
  // Modulo: for the render between a list swap and the reset below, the old
  // index may be past the end of the new list.
  const activeWord = TYPEWRITER_WORDS[currentWordIndex % TYPEWRITER_WORDS.length] || '';

  // A new list (language or breakpoint change) starts from its first word.
  useEffect(() => {
    setCurrentWordIndex(0);
    setCurrentCharIndex(0);
    setIsDeleting(false);
  }, [TYPEWRITER_WORDS]);

  // Category bubbles — driven by the REAL category taxonomy (same source as
  // the sidebar), navigated by stable English key. Replaces the old approach
  // of dropping a translated label into ?search=, which returned different
  // (and wrong) products per language. See useCategoryBubbles for the why.
  const categoryBubbles = useCategoryBubbles(7);

  // Size the hero to exactly the remaining viewport. It measures its own top
  // edge (robust to the header/banner/ticker above it changing height) and
  // reserves room for the fixed mobile bottom tab bar, so the hero owns exactly
  // one screen. We re-measure on resize/orientation/load and via a
  // ResizeObserver because the chrome above the hero can render in
  // asynchronously (which would otherwise leave the hero taller than the space
  // actually left → page scroll).
  useEffect(() => {
    const updateHeight = () => {
      const el = heroRef.current;
      if (!el) return;
      const offsetTop = el.getBoundingClientRect().top + window.scrollY;
      // The bottom tab bar only exists on mobile widths, and Nav renders in
      // both the desktop and mobile trees — take whichever instance is
      // on-screen (the hidden one reports offsetHeight 0).
      let bottomReserve = 0;
      document.querySelectorAll('[data-mobile-tabbar]').forEach((bar) => {
        bottomReserve = Math.max(bottomReserve, bar.offsetHeight);
      });
      const next = `calc(100dvh - ${Math.round(offsetTop + bottomReserve)}px)`;
      setHeroHeight((prev) => (prev === next ? prev : next));
    };
    updateHeight();
    window.addEventListener('resize', updateHeight, { passive: true });
    window.addEventListener('orientationchange', updateHeight);
    window.addEventListener('load', updateHeight);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateHeight) : null;
    if (ro) ro.observe(document.body);
    const t1 = setTimeout(updateHeight, 200);
    const t2 = setTimeout(updateHeight, 800);
    return () => {
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('orientationchange', updateHeight);
      window.removeEventListener('load', updateHeight);
      if (ro) ro.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  // Bestsellers for the hero showcase — the top lane of the showcase on desktop
  // and the horizontal rail inside the mobile panel. Both trees render it, so
  // unlike the earlier desktop-only version this is no longer gated on a
  // viewport width.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchBestsellers(1, HERO_BESTSELLER_COUNT * 2);
        if (cancelled) return;
        const list = (res?.success ? res.data?.products || [] : [])
          .filter((p) => p && (p.pharmacode || p._id))
          .slice(0, HERO_BESTSELLER_COUNT);
        setBestsellers(list);
      } catch (error) {
        // Non-critical decoration — an empty list simply hides the showcase.
        if (!cancelled) setBestsellers([]);
      } finally {
        if (!cancelled) setBestsellersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live offers for the desktop showcase's lower lane. 'none' → standalone
  // (non-campaign) discounts, the same feed as the Discount section.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchAllDiscounts(1, HERO_DEAL_COUNT, 'none');
        if (!cancelled) setDeals(res?.success ? res.data?.products || [] : []);
      } catch (error) {
        if (!cancelled) setDeals([]);
      } finally {
        if (!cancelled) setDealsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Typewriter effect - respect reduced motion preference (optimized)
  useEffect(() => {
    // Skip animation if user prefers reduced motion
    if (prefersReducedMotion.current) {
      setCurrentCharIndex(activeWord.length);
      return;
    }

    if (isSearchFocused) return; // Stop animation when search is focused

    const typeSpeed = isDeleting ? TYPEWRITER_DELETE_SPEED : TYPEWRITER_SPEED;

    // Use requestAnimationFrame for smoother performance
    let rafId = null;
    const timer = setTimeout(() => {
      rafId = requestAnimationFrame(() => {
        if (!isDeleting && currentCharIndex < activeWord.length) {
          setCurrentCharIndex(prev => prev + 1);
        } else if (!isDeleting && currentCharIndex >= activeWord.length) {
          setTimeout(() => setIsDeleting(true), TYPEWRITER_PAUSE_TIME);
        } else if (isDeleting && currentCharIndex > 0) {
          setCurrentCharIndex(prev => prev - 1);
        } else if (isDeleting && currentCharIndex === 0) {
          setIsDeleting(false);
          setCurrentWordIndex(prev => (prev + 1) % TYPEWRITER_WORDS.length);
        }
      });
    }, typeSpeed);

    return () => {
      clearTimeout(timer);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [currentCharIndex, currentWordIndex, isDeleting, isSearchFocused, activeWord, TYPEWRITER_WORDS.length]);

  // Search functionality
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowPreview(false);
        setShowBubbles(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  useEffect(() => {
    // Cancel previous request if it exists
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }

    const trimmedSearch = searchTerm.trim();

    if (trimmedSearch.length >= SEARCH_MIN_LENGTH) {
      setIsLoading(true);
      setShowPreview(true);
      setShowBubbles(false); // Hide bubbles when typing

      // Create new AbortController for this request
      const abortController = new AbortController();
      searchAbortControllerRef.current = abortController;

      const fetchResults = async () => {
        try {
          const response = await searchProducts(trimmedSearch, 1, 5, '', 'random', '');

          // Check if request was aborted
          if (abortController.signal.aborted) return;

          const ms = response?.success ? (response.data.matchedSymptom || null) : null;
          setMatchedSymptom(ms);

          if (ms) {
            // The text search for a symptom word (e.g. "headache") matches
            // nothing in the German catalog — preview the symptom's products.
            const symResp = await getProductsBySymptom(ms.key, 1, 5);
            if (abortController.signal.aborted) return;
            const prods = symResp?.success ? (symResp.data.products || []) : [];
            setPreviewResults(prods);
            setDidYouMean(null);
            setAnnounceSearchResults(`${prods.length} ${prods.length === 1 ? 'Ergebnis' : 'Ergebnisse'} gefunden`);
          } else if (response?.success && Array.isArray(response.data.products)) {
            setPreviewResults(response.data.products);
            setDidYouMean(response.data.didYouMean || null);
            // Announce results to screen readers
            const count = response.data.products.length;
            setAnnounceSearchResults(`${count} ${count === 1 ? 'Ergebnis' : 'Ergebnisse'} gefunden`);
          } else {
            setPreviewResults([]);
            setDidYouMean(null);
            setAnnounceSearchResults('Keine Ergebnisse gefunden');
          }
        } catch (error) {
          // Don't handle aborted requests as errors
          if (!abortController.signal.aborted) {
            setPreviewResults([]);
            setDidYouMean(null);
            setMatchedSymptom(null);
            setAnnounceSearchResults('Fehler bei der Suche');
          }
        } finally {
          if (!abortController.signal.aborted) {
            setIsLoading(false);
          }
        }
      };

      const timer = setTimeout(fetchResults, SEARCH_DEBOUNCE_MS);
      return () => {
        clearTimeout(timer);
        abortController.abort();
      };
    } else {
      setPreviewResults([]);
      setShowPreview(false);
      setIsLoading(false);
      setMatchedSymptom(null);
      setAnnounceSearchResults('');
    }
  }, [searchTerm]);

  const handleSearchSubmit = useCallback((e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      if (matchedSymptom) {
        navigate(`/products?symptom=${matchedSymptom.key}`);
      } else {
        const searchParams = new URLSearchParams();
        searchParams.append('search', searchTerm);
        navigate(`/products?${searchParams.toString()}`);
      }
      setShowPreview(false);
      setTimeout(() => scrollToTop(), 100);
    }
  }, [searchTerm, navigate, matchedSymptom]);

  const handlePreviewClick = useCallback((pharmacode) => {
    navigate(`/product/${pharmacode}`);
    setSearchTerm('');
  }, [navigate]);

  const handleBubbleClick = useCallback((bubble) => {
    setShowBubbles(false);
    // Route to the real category listing by stable English key — never a
    // translated search string, so results are correct and language-independent.
    goToCategory(navigate, bubble?.key, bubble?.de);
    setTimeout(() => scrollToTop(), 100);
  }, [navigate]);

  // The showcase column only earns its space once it has something to show —
  // if the bestseller request fails or comes back empty the copy takes the
  // full width instead of leaving a hole on the right.
  const showcaseVisible = bestsellersLoading || bestsellers.length > 0;
  // Desktop only: the right half stays while either of its two lanes has
  // something to show.
  const desktopShowcaseVisible = showcaseVisible || dealsLoading || deals.length > 0;

  const currentDisplayText = useMemo(() =>
    activeWord.slice(0, currentCharIndex),
    [activeWord, currentCharIndex]
  );

  // Helper to build article data from product
  const buildArticle = (product) => {
    const firstArticle = product.articles?.[0];
    let imageArray = [];
    if (firstArticle && Array.isArray(firstArticle.images)) {
      imageArray = firstArticle.images;
    } else if (Array.isArray(product.images)) {
      imageArray = product.images;
    } else if (firstArticle && Array.isArray(firstArticle.images?.F)) {
      imageArray = firstArticle.images.F;
    }
    return {
      pharmacode: firstArticle?.pharmacode || product._id,
      description: {
        description: firstArticle?.description?.description || product.description?.description || 'No description available',
        longDescription: firstArticle?.description?.longDescription || product.description?.longDescription || product.longDescription || 'No description available'
      },
      hasPhoto: firstArticle?.hasPhoto || product.hasPhoto || false,
      images: { F: imageArray.map(img => img.replace(/^uploads[\\/]/, '')) },
      price: firstArticle?.price ?? null
    };
  };

  // Shared search preview dropdown
  const renderSearchPreview = (maxHeight = '320px') => (
    <AnimatePresence>
      {showPreview && searchTerm.trim().length >= 2 && (
        <motion.div
          initial={{ opacity: 0, y: -5, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          className="absolute z-[99999] w-full mt-3 bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-200/50 overflow-hidden"
          style={{ maxHeight }}
          id="search-preview"
          role="listbox"
          aria-label="Suchergebnisse"
        >
          {matchedSymptom && !isLoading && (
            <button
              type="button"
              onClick={() => {
                navigate(`/products?symptom=${matchedSymptom.key}`);
                setShowPreview(false);
                setTimeout(() => scrollToTop(), 100);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 border-b border-brand-tint bg-brand-tint/60 text-left transition-colors hover:bg-brand-tint"
            >
              <span className="text-xl">🩹</span>
              <span className="flex-1 text-sm">
                <span className="font-semibold text-brand">{matchedSymptom.label}</span>
                <span className="text-brand"> — {t('searchBar.symptomShortcut')}</span>
              </span>
              <span className="text-brand">→</span>
            </button>
          )}
          {isLoading ? (
            <div className="p-8 text-center" role="status" aria-live="polite">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand border-r-transparent" aria-hidden="true"></div>
              <p className="mt-2 text-gray-500">
                <span className="sr-only">Suche wird durchgeführt, bitte warten</span>
                <span aria-hidden="true">Suche läuft...</span>
              </p>
            </div>
          ) : previewResults.length > 0 ? (
            <motion.div
              className="py-2 divide-y divide-gray-100 max-h-72 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
            >
              {previewResults.map((product) => {
                const article = buildArticle(product);
                const priceShape = getPriceShape(article);
                const ariaPrice = priceShape.currentPrice != null
                  ? `CHF ${formatCHF(priceShape.currentPrice)}`
                  : 'Preis nicht verfügbar';
                return (
                  <motion.div
                    key={product._id}
                    variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    whileHover={{ scale: 1.01, backgroundColor: "#f9fafb" }}
                    onClick={() => { setClickSource('search'); handlePreviewClick(article.pharmacode); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setClickSource('search'); handlePreviewClick(article.pharmacode); } }}
                    role="option"
                    tabIndex={0}
                    aria-label={`${article.description.longDescription}, Preis: ${ariaPrice}`}
                    className="px-4 lg:px-6 py-3 lg:py-4 hover:bg-gray-50/80 cursor-pointer flex items-center space-x-3 lg:space-x-4 transition-colors duration-150 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-brand/40 focus:ring-inset"
                  >
                    <div className="w-12 h-12 lg:w-16 lg:h-16 flex-shrink-0 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-200">
                      {article.hasPhoto && article.images.F.length > 0 ? (
                        <img src={getOptimizedImageUrl(article.images.F) || ''} alt={article.description.description} className="w-full h-full object-contain" loading="lazy" decoding="async" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-gray-100/80 to-gray-200/80 flex items-center justify-center">
                          <svg className="w-6 h-6 lg:w-8 lg:h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 line-clamp-2">{article.description.longDescription}</p>
                      <PriceDisplay article={article} variant="compact" className="mt-0.5 lg:mt-1" />
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          ) : (
            <div className="p-6 lg:p-8 text-center text-gray-500">
              {didYouMean ? (
                <div>
                  <p className="mb-4 text-gray-500">Keine Ergebnisse gefunden für "{searchTerm}"</p>
                  <div className="p-4 lg:p-6 rounded-2xl border border-brand/15 bg-brand-tint/60">
                    <div className="flex items-center justify-center space-x-2 mb-3">
                      <svg className="w-5 h-5 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm font-semibold text-gray-900">Meinten Sie:</p>
                    </div>
                    <button
                      onClick={() => { setSearchTerm(didYouMean); navigate(`/products?search=${didYouMean}`); setShowPreview(false); }}
                      className="inline-flex items-center px-4 py-2 rounded-full bg-brand text-white text-sm font-semibold transition-all duration-300 hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-brand/25 focus:ring-offset-2"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      "{didYouMean}"
                    </button>
                  </div>
                </div>
              ) : (
                <p>Keine Ergebnisse gefunden für "{searchTerm}"</p>
              )}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Shared category chips.
  //
  // `layout: 'halo'` (desktop) rings them around the search bar instead of
  // stacking them underneath: one row above the field, one below, each a flex
  // row so chips can never land on top of each other regardless of how long a
  // translated category name is. Per-chip vertical jitter and tilt supply the
  // scattered feel that free positioning was supposed to give.
  // `layout: 'inline'` (mobile) keeps a simple centred wrap.
  const renderBubbles = (compact = false, layout = 'inline') => {
    const isHalo = layout === 'halo';
    const above = isHalo ? categoryBubbles.slice(0, 3) : categoryBubbles;
    const below = isHalo ? categoryBubbles.slice(3) : [];

    const Chip = ({ bubble, index, fromY }) => {
      const jitter = BUBBLE_JITTER[index % BUBBLE_JITTER.length];
      // Every third chip carries the brand fill so the ring reads red-accented
      // rather than a row of plain white pills.
      const accent = index % 3 === 1;
      return (
        <motion.button
          type="button"
          initial={isHalo
            ? { opacity: 0, scale: 0.3, y: fromY, rotate: 0 }
            : { opacity: 0, scale: 0.3, y: 20 }}
          animate={isHalo
            ? { opacity: 1, scale: 1, y: jitter.y, rotate: jitter.rotate }
            : { opacity: 1, scale: 1, y: 0 }}
          exit={isHalo
            ? { opacity: 0, scale: 0.3, y: fromY, rotate: 0 }
            : { opacity: 0, scale: 0.3, y: -20 }}
          transition={{ type: 'spring', stiffness: 260, damping: 18, delay: index * 0.045 }}
          whileHover={{ scale: 1.12, rotate: 0, y: jitter.y - 4, transition: { type: 'spring', stiffness: 400, damping: 15 } }}
          whileTap={{ scale: 0.95 }}
          onClick={() => handleBubbleClick(bubble)}
          aria-label={bubble.label}
          className={`
            pointer-events-auto inline-flex items-center gap-1.5 rounded-full font-medium whitespace-nowrap
            cursor-pointer select-none backdrop-blur-sm
            shadow-[0_10px_24px_-12px_rgb(var(--brand)/0.45)]
            focus:outline-none focus:ring-2 focus:ring-brand/25 focus:ring-offset-2
            ${compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'}
            ${accent
              ? 'border border-white/60 bg-brand text-white hover:bg-brand-hover'
              : 'border border-brand/15 bg-white/95 text-gray-700 hover:border-brand/25 hover:bg-brand-tint hover:text-brand'}
          `}
        >
          <span
            className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${accent ? 'bg-white/80' : 'bg-brand landing:bg-petal'}`}
            aria-hidden="true"
          />
          {bubble.label}
        </motion.button>
      );
    };

    return (
      <AnimatePresence>
        {showBubbles && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className={isHalo
              ? 'pointer-events-none absolute inset-0 z-[99998]'
              : 'absolute z-[99998] w-full mt-3'}
          >
            {isHalo ? (
              <div className="relative h-full w-full">
                <div className="absolute inset-x-0 -top-[3.75rem] flex flex-wrap items-center justify-center gap-2.5">
                  {above.map((bubble, i) => (
                    <Chip key={bubble.id} bubble={bubble} index={i} fromY={64} />
                  ))}
                </div>
                <div className="absolute inset-x-0 top-[5.75rem] flex flex-wrap items-center justify-center gap-2.5">
                  {below.map((bubble, i) => (
                    <Chip key={bubble.id} bubble={bubble} index={i + above.length} fromY={-64} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="relative flex flex-wrap justify-center gap-2 px-2">
                {above.map((bubble, i) => (
                  <Chip key={bubble.id} bubble={bubble} index={i} fromY={0} />
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  return (
    <>
      <style>{`
        /* Mobile bestseller rail: scrolls horizontally without showing a
           scrollbar, and snaps so cards don't come to rest half-cut. */
        .hero-rail { scrollbar-width: none; -ms-overflow-style: none; scroll-snap-type: x proximity; }
        .hero-rail::-webkit-scrollbar { display: none; }
        .hero-rail > div > * { scroll-snap-align: start; }
        /* Slow Ken Burns drift on the desktop stage photo. (The reduced-motion
           rule below stops it.) */
        @keyframes hero-kenburns {
          from { transform: scale(1.02) translate3d(0, 0, 0); }
          to   { transform: scale(1.09) translate3d(-1.5%, -1%, 0); }
        }
        .hero-kenburns { animation: hero-kenburns 24s ease-in-out infinite alternate; will-change: transform; }
        /* Gentle bob for the category chips under the search bar. */
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          25% { transform: translateY(-8px) rotate(1deg); }
          50% { transform: translateY(-4px) rotate(0deg); }
          75% { transform: translateY(-12px) rotate(-1deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
      {/* Skip to main content link for keyboard navigation */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[99999] focus:px-4 focus:py-2 focus:bg-brand focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand/40 focus:ring-offset-2"
      >
        Zum Hauptinhalt springen
      </a>
      {/* Live region for screen reader announcements */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {announceSearchResults}
      </div>

      {/* The hero root stays white: on mobile that white is the sheet the
          search pill and the two rails sit on below the red canvas, and on
          desktop it is simply the page under the full-bleed split. */}
      {/* Phones: at least one screen, but free to grow — a fixed height clipped
          the top of the red canvas (the logo) on tall-content phones such as an
          iPhone in Safari. Desktop keeps the exact one-screen height. */}
      <div
        ref={heroRef}
        className="relative flex min-h-[var(--hero-h)] w-full flex-col bg-white lg:h-[var(--hero-h)] lg:min-h-0"
        style={{ '--hero-h': heroHeight }}
        id="main-content"
      >
        <div className="relative flex w-full flex-1 items-stretch justify-center overflow-hidden">
          {/* ===== MOBILE & TABLET LAYOUT — immersive canvas =====
              Not a stacked, centred version of the desktop copy any more. The
              hero splits into two surfaces the way the desktop splits into two
              halves — only here the seam is horizontal: a brand-red canvas
              carrying the whole message, and a white sheet carrying the tools
              (search, categories, products). The search pill straddles the seam
              so the two surfaces are stitched rather than stacked, and both
              rails run off the right edge so the screen reads as swipeable
              rather than as a finished poster.

              The canvas is `flex-1 min-h-0`: it absorbs whatever height is left
              after the pill and the two rails have taken theirs, which is what
              keeps the whole hero inside exactly one screen on a 568px phone as
              well as a 932px one. */}
          <motion.div className="relative z-10 flex w-full flex-col lg:hidden">
            {/* ---------- The canvas ---------- */}
            {/* Flat brand red with the shared red-surface decoration (grain,
                light, the cropped cross) — the same card as the desktop hero's
                message card, run full-bleed so the red header flows into it. */}
            <div className="relative flex min-h-0 flex-1 flex-col justify-end rounded-b-[2rem] bg-brand px-5 pb-14 pt-6">
              <RedSurfaceDecor
                radius="rounded-b-[2rem]"
                crossClassName="absolute -right-[14%] -top-[8%] h-[70%] w-auto rotate-[-10deg] text-white/[0.09]"
              />

              <motion.div
                className="relative"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* Same opening as the desktop card: the mark knocked out to
                    white (in full colour its red rose would vanish into the
                    red), then the two-line headline. Sized in vh so the hero
                    still fits one screen on a short phone. */}
                <img
                  src="/images/dreirosen-logo.svg"
                  alt="Dreirosen Apotheke"
                  width="512"
                  height="512"
                  loading="eager"
                  decoding="async"
                  className="h-[clamp(3.25rem,9vh,5.5rem)] w-auto brightness-0 invert drop-shadow-[0_8px_18px_rgb(var(--brand-shadow-deep)/0.45)]"
                />

                <h1 className="mt-[clamp(1rem,3vh,1.75rem)] text-[clamp(1.85rem,5.6vh,3rem)] font-extrabold leading-[0.95] tracking-[-0.045em] text-white">
                  <span className="block">{t('hero.titlePrefix')}</span>
                  {/* min-h + the zero-width space keep this line's box from
                      collapsing while the typewriter deletes back to nothing. */}
                  <span
                    className="mt-1 flex min-h-[1em] items-baseline"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    <span className="pb-[0.08em] pr-[0.12em]">
                      {(prefersReducedMotion.current ? activeWord : currentDisplayText) || '​'}
                    </span>
                    {!prefersReducedMotion.current && (
                      <span
                        className={`ml-1 inline-block w-[3px] rounded-full bg-white landing:bg-petal ${!isSearchFocused ? 'animate-pulse' : ''}`}
                        style={{ height: '0.8em' }}
                        aria-hidden="true"
                      />
                    )}
                  </span>
                </h1>

                <p className="mt-3 max-w-[20rem] text-[13.5px] leading-relaxed text-white/80">
                  {t('hero.descriptionMobile')}
                </p>

                {/* Trust — white checks in glass discs, as on desktop. */}
                <ul className="mt-4 flex flex-wrap items-center gap-x-3.5 gap-y-2">
                  {[t('hero.trust.prescriptionFree'), t('hero.trust.nationwide'), t('hero.trust.topPrices')].map((label) => (
                    <li key={label} className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-white/90">
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/20 ring-1 ring-inset ring-white/25" aria-hidden="true">
                        <HiCheck className="h-2.5 w-2.5 text-white" />
                      </span>
                      {label}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* ---------- Search, straddling the seam ----------
                `mx-5` rather than `px-5`: the bubbles and the results preview
                are absolutely positioned `w-full` inside this box, and a
                percentage width resolves against the padding box — with padding
                they would hang 20px past the right edge. */}
            <motion.div
              ref={searchRef}
              className="relative z-20 mx-5 -mt-7"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.18 }}
            >
              <form onSubmit={handleSearchSubmit} aria-label={t('hero.searchForm')}>
                <label htmlFor="hero-search-input-mobile" className="sr-only">
                  {t('hero.searchLabel')}
                </label>
                <div className="flex w-full items-center gap-2 rounded-full border border-white/70 bg-white p-1.5 pl-4 shadow-[0_22px_50px_-20px_rgb(var(--brand-shadow)/0.65)] transition-all duration-300 focus-within:border-brand/25 focus-within:ring-4 focus-within:ring-brand/10">
                  <HiOutlineSearch className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                  <input
                    id="hero-search-input-mobile"
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => { setIsSearchFocused(true); setShowBubbles(true); }}
                    onBlur={() => setIsSearchFocused(false)}
                    className="min-w-0 flex-1 border-none bg-transparent py-2.5 text-base text-gray-900 placeholder-gray-400 shadow-none focus:outline-none focus:ring-0"
                    style={{ outline: 'none', boxShadow: 'none' }}
                    placeholder={t('hero.searchPlaceholder')}
                    aria-label={t('hero.searchLabel')}
                    role="combobox"
                    aria-expanded={showPreview}
                    aria-controls="search-preview"
                    aria-autocomplete="list"
                    autoComplete="off"
                  />
                  <button
                    type="submit"
                    aria-label={t('nav.bottom.search')}
                    className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand text-white transition-all duration-300 hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
                  >
                    <HiArrowRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </form>
              {renderBubbles(true, 'inline')}
              {renderSearchPreview('250px')}
            </motion.div>

            {/* ---------- Help strip ----------
                The same "what's bothering you today?" tiles as the desktop
                hero. The bestseller rail that used to sit here is now its own
                full panel directly below the hero, so it is not repeated. */}
            <motion.div
              className={`mx-3 mt-5 transition-opacity duration-200 ${
                (showBubbles || showPreview) ? 'pointer-events-none opacity-0' : 'opacity-100'
              }`}
              aria-hidden={showBubbles || showPreview}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.34, ease: [0.22, 1, 0.36, 1] }}
            >
              <HeroHelpStrip />
            </motion.div>
          </motion.div>

          {/* ===== DESKTOP LAYOUT — copy | showcase =====
              The copy and search on the left, a two-lane product showcase
              (bestsellers over live offers) on the right. See HeroShowcase.
              The hero scrolls away as-is — there is deliberately no
              scroll-linked fade on it. */}
          <div className="relative z-10 hidden w-full lg:flex">
              {/* ---------- LEFT: the photo stage ----------
                  One immersive card beside the showcase panel: the family
                  photo full-bleed, and over it the headline, the search, the
                  CTA and the symptom tiles as a glass dock (see below).
                  The column is a flex column sized by the row (not by its
                  content), so the stage fills the hero's height. The padding
                  matches the right panel's, which is what makes the gutters
                  line up. */}
              <motion.div
               
                className={`relative z-20 flex flex-col gap-[clamp(0.6rem,1.6vh,1rem)] py-[clamp(0.75rem,2.5vh,1.75rem)] pl-[clamp(0.75rem,1.6vw,2rem)] ${
                  desktopShowcaseVisible ? 'w-1/2 pr-[clamp(0.6rem,1vw,1rem)] xl:w-[45%]' : 'w-full pr-[clamp(0.75rem,1.6vw,2rem)]'
                }`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* ===== The stage: one immersive photo card =====
                    The family photo runs full-bleed behind everything, with a
                    deep-green scrim rising from the foot so white type reads on
                    it. Over it, bottom-anchored like a magazine cover: the
                    headline, the search, the CTA with the trust chips, and the
                    symptom tiles as a frosted-glass dock.

                    Deliberately NOT overflow-hidden: the search preview and the
                    category halo hang out of the search field. Everything that
                    must be clipped (photo, scrim, glow) lives in its own clipped
                    layer. */}
                <div className="relative z-20 flex min-h-0 flex-1 flex-col rounded-[2rem] shadow-[0_30px_60px_-36px_rgb(var(--brand-shadow-deep)/0.7)]">
                  <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-[2rem]">
                    <img
                      src="/images/hero/family-park.jpg"
                      alt=""
                      loading="eager"
                      decoding="async"
                      className="hero-kenburns absolute inset-0 h-full w-full object-cover object-[42%_30%]"
                    />
                    {/* Scrim: clear at the top (the faces), deep green at the foot. */}
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          'linear-gradient(180deg, rgb(var(--brand-shadow-deep)/0.05) 0%, rgb(var(--brand-deep)/0.3) 32%, rgb(var(--brand-deep)/0.82) 62%, rgb(var(--brand-deep)/0.95) 100%)',
                      }}
                    />
                    {/* …and from the left, where the copy sits, so it reads over
                        the faces at mid heights. */}
                    <div
                      className="absolute inset-0"
                      style={{ background: 'linear-gradient(90deg, rgb(var(--brand-shadow-deep)/0.45) 0%, rgb(var(--brand-shadow-deep)/0.15) 45%, transparent 75%)' }}
                    />
                    {/* A soft logo-green glow from the lower left, so the scrim
                        is a colour rather than just "darker". */}
                    <div
                      className="absolute -bottom-[25%] -left-[15%] h-[70%] w-[70%] rounded-full"
                      style={{ background: 'radial-gradient(closest-side, rgb(var(--brand-light)/0.35), transparent)' }}
                    />
                    <div className="absolute inset-0 rounded-[2rem] ring-1 ring-inset ring-white/10" />
                  </div>

                  {/* Top: the "from home" badge, as a glass chip. */}
                  <div className="absolute left-0 top-0 z-10 p-[clamp(0.9rem,1.6vw,1.5rem)] [@media(max-height:940px)]:hidden">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 py-1.5 pl-1.5 pr-3 text-[12px] font-semibold text-white ring-1 ring-inset ring-white/25 backdrop-blur-md">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-brand landing:text-petal" aria-hidden="true">
                        <HiOutlineHome className="h-3.5 w-3.5" />
                      </span>
                      {t('hero.photo.badge')}
                    </span>
                  </div>

                  {/* Bottom-anchored copy, search and dock. */}
                  <div className="relative mt-auto flex flex-col px-[clamp(1.1rem,2.2vw,2.25rem)] pb-[clamp(0.9rem,1.6vw,1.5rem)] pt-[clamp(0.9rem,1.6vw,1.5rem)]">
                    <p className="max-w-[34rem] text-[clamp(1.9rem,4.6vh,3.4rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-white [text-wrap:balance]">
                      {t('hero.photo.title')}
                    </p>
                    <h1 className="mt-[clamp(0.4rem,1.2vh,0.75rem)] max-w-[32rem] text-[clamp(13.5px,1.8vh,16px)] font-medium leading-relaxed text-white/80 [@media(max-height:820px)]:sr-only">
                      {t('hero.descriptionMobile')}
                    </h1>

                    {/* Search — white pill on the photo, brand submit. While the
                        field is empty and unfocused it types example searches
                        ("Search for Ibuprofen|"); the moment it is focused the
                        animation stops and gives way to the plain placeholder. */}
                    <div ref={searchRef} className="relative mt-[clamp(0.8rem,2.2vh,1.4rem)] w-full max-w-xl">
                      <form onSubmit={handleSearchSubmit} aria-label={t('hero.searchForm')}>
                        <label htmlFor="hero-search-input" className="sr-only">
                          {t('hero.searchLabelLong')}
                        </label>
                        <div className="group/search flex w-full items-center gap-3 rounded-full bg-white p-1.5 pl-5 shadow-[0_22px_44px_-18px_rgba(0,0,0,0.55)] ring-1 ring-white/60 transition-all duration-300 focus-within:ring-4 focus-within:ring-brand-light/40">
                          <HiOutlineSearch className="h-5 w-5 flex-shrink-0 text-gray-400 transition-colors duration-200 group-focus-within/search:text-brand" aria-hidden="true" />
                          <div className="relative min-w-0 flex-1">
                          <input
                            id="hero-search-input"
                            type="search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onFocus={() => { setIsSearchFocused(true); setShowBubbles(true); }}
                            onBlur={() => setIsSearchFocused(false)}
                            className="w-full border-none bg-transparent p-0 py-[clamp(0.5rem,1.4vh,0.8rem)] text-base text-gray-900 placeholder-gray-400 shadow-none focus:outline-none focus:ring-0"
                            style={{ outline: 'none', boxShadow: 'none' }}
                            placeholder={isSearchFocused ? t('hero.searchPlaceholder') : ''}
                            aria-label={t('hero.searchLabel')}
                            role="combobox"
                            aria-expanded={showPreview}
                            aria-autocomplete="list"
                            aria-controls="search-preview"
                            aria-describedby="search-description"
                            autoComplete="off"
                          />
                          {/* Animated placeholder. Decorative (the input keeps
                              its aria-label); fades out on focus or typing. */}
                          <span
                            aria-hidden="true"
                            className={`pointer-events-none absolute inset-0 flex items-center overflow-hidden whitespace-nowrap text-base text-gray-400 transition-opacity duration-200 ${
                              !isSearchFocused && !searchTerm ? 'opacity-100' : 'opacity-0'
                            }`}
                          >
                            {t('hero.searchTypingPrefix')}&nbsp;
                            <span>
                              {prefersReducedMotion.current ? activeWord : currentDisplayText}
                            </span>
                            {!prefersReducedMotion.current && (
                              <span className="ml-0.5 inline-block h-[1.15em] w-[2px] animate-pulse rounded-full bg-brand landing:bg-petal" />
                            )}
                          </span>
                          </div>
                          <button
                            type="submit"
                            aria-label={t('nav.bottom.search')}
                            className="group/btn inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-brand text-white transition-all duration-300 hover:bg-brand-hover hover:shadow-lg hover:shadow-brand/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2"
                          >
                            <HiArrowRight className="h-5 w-5 transition-transform duration-300 group-hover/btn:translate-x-0.5" aria-hidden="true" />
                          </button>
                        </div>
                        <div id="search-description" className="sr-only">
                          {t('hero.searchHint')}
                        </div>
                      </form>
                      {renderBubbles(false, 'halo')}
                      {renderSearchPreview('320px')}
                    </div>

                    {/* CTA + trust chips. Fade out while the search is active so
                        the category halo / preview own the space below the field. */}
                    <div
                      className={`mt-[clamp(0.7rem,1.8vh,1.1rem)] flex flex-wrap items-center gap-x-3 gap-y-2 transition-opacity duration-200 ${
                        (showBubbles || showPreview) ? 'pointer-events-none opacity-0' : 'opacity-100'
                      }`}
                      aria-hidden={showBubbles || showPreview}
                    >
                      <button
                        type="button"
                        onClick={() => { navigate('/products'); setTimeout(() => scrollToTop(), 100); }}
                        className="group inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-brand shadow-[0_14px_30px_-14px_rgba(0,0,0,0.6)] transition-all duration-300 hover:-translate-y-px hover:bg-brand-tint focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-brand"
                      >
                        {t('hero.ctaDiscover')}
                        <HiArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                      </button>

                      <ul className="flex flex-wrap items-center gap-1.5 [@media(max-height:820px)]:hidden">
                        {[
                          t('hero.trust.prescriptionFree'),
                          t('hero.trust.nationwide'),
                          t('hero.trust.topPrices'),
                        ].map((label) => (
                          <li key={label} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 py-1 pl-1 pr-2.5 text-[12px] font-semibold text-white/90 ring-1 ring-inset ring-white/20 backdrop-blur-md">
                            <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-brand-light" aria-hidden="true">
                              <HiCheck className="h-2.5 w-2.5 text-white" />
                            </span>
                            {label}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* The symptom tiles, as a glass dock at the foot of the stage. */}
                    <div className="relative z-10 mt-[clamp(0.8rem,2.2vh,1.4rem)]">
                      <HeroHelpStrip variant="glass" />
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* ---------- RIGHT: bestsellers + offers ----------
                  A soft, inset panel rather than a full-bleed colour block, so
                  the product cards are the colour. Absolute, not h-full: the
                  half's height comes from flex stretch, which a percentage
                  height cannot resolve against, and the lanes must contribute
                  no intrinsic height — the row height is the copy side's. */}
              {desktopShowcaseVisible && (
                <div className="relative w-1/2 xl:w-[55%]">
                  <motion.div
                   
                    className="absolute inset-0 py-[clamp(0.75rem,2.5vh,1.75rem)] pr-[clamp(0.75rem,1.6vw,2rem)]"
                    initial={{ opacity: 0, x: 32 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <div className="relative h-full overflow-hidden rounded-[2rem] bg-[#F6F3F0] px-[clamp(0.75rem,1.6vw,1.75rem)] py-[clamp(0.75rem,2.4vh,1.75rem)] ring-1 ring-black/[0.04]">
                      {/* Faint dot grid so the panel reads as a surface, not
                          as an empty grey box. */}
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0"
                        style={{
                          backgroundImage: 'radial-gradient(rgba(17,24,39,0.07) 1px, transparent 1px)',
                          backgroundSize: '18px 18px',
                        }}
                      />
                      <div className="relative h-full">
                        <HeroShowcase
                          bestsellers={bestsellers}
                          bestsellersLoading={bestsellersLoading}
                          deals={deals}
                          dealsLoading={dealsLoading}
                          onSelect={handlePreviewClick}
                        />
                      </div>
                    </div>
                  </motion.div>
                </div>
              )}
          </div>
        </div>
      </div>
    </>
  );
}

export default HeroComponent;
